import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "../../components/Layout/Layout";
import {
  Card,
  Button,
  Badge,
  Input,
  Select,
  Modal,
  Spinner,
  ImagePreviewModal,
  isImageAttachment,
} from "../../components/UI";
import api, { leadsAPI, callLogsAPI, usersAPI, pipelineAPI, filePublicUrl } from "../../services/api";
import toast from "react-hot-toast";
import {
  HiOutlineArrowLeft,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlinePhone,
  HiOutlineMail,
  HiOutlineSave,
  HiOutlineUserAdd,
  HiOutlineOfficeBuilding,
  HiOutlineLocationMarker,
  HiOutlineGlobeAlt,
  HiOutlineIdentification,
  HiOutlineCurrencyDollar,
  HiOutlineTag,
  HiOutlineUser,
  HiOutlineUsers,
  HiOutlinePlay,
  HiOutlineDownload,
  HiOutlinePaperClip,
} from "react-icons/hi";
import { format } from "date-fns";

const statusOptions = [
  { value: "new", label: "New" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "follow_up", label: "Follow Up" },
  { value: "cold", label: "Cold" },
  { value: "warm", label: "Warm" },
  { value: "hot", label: "Hot" },
];

// Helper function to format notes with proper line breaks and timestamp formatting
const formatNotes = (notes) => {
  if (!notes) return null;

  // Split by newlines
  const lines = notes.split('\n');

  return lines.map((line, index) => {
    // Format timestamps like [2026-06-02T10:00:55.645Z] to readable format
    const formattedLine = line.replace(
      /\[(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z)\]/g,
      (match, timestamp) => {
        try {
          const date = new Date(timestamp);
          return `[${format(date, 'MMM d, yyyy h:mm a')}]`;
        } catch {
          return match;
        }
      }
    );

    // Render each line with a line break
    return (
      <React.Fragment key={index}>
        {formattedLine}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

// Displays a labeled value row with an optional leading icon.
const InfoField = ({ label, value, icon }) => (
  <div>
    <p className="text-sm text-gray-500">{label}</p>
    <div className="flex items-center gap-2 mt-1">
      {icon && <span className="text-gray-400 flex-shrink-0">{icon}</span>}
      <span className="font-medium text-gray-900 break-words">
        {value || "-"}
      </span>
    </div>
  </div>
);

// Joins non-empty address parts into a single readable string.
const formatAddress = (address) => {
  if (!address) return "";
  const parts = [address.street, address.city, address.state, address.pincode, address.country]
    .map((p) => (p || "").trim())
    .filter(Boolean);
  return parts.join(", ");
};

const LeadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});

  // Recording playback state for the Recent Calls list.
  // recordingLog holds the call log currently being played; audioUrl holds
  // the object URL of the fetched blob (auth-protected endpoint).
  const [recordingLog, setRecordingLog] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Fetch the recording blob (auth-protected) whenever a call is selected to play.
  useEffect(() => {
    if (!recordingLog?.recordingPublicId) return;

    let revokeable = null;
    const fetchAudio = async () => {
      try {
        const response = await api.get(
          `/recordings/file/${recordingLog.recordingPublicId}`,
          { responseType: "blob" }
        );
        const url = URL.createObjectURL(response.data);
        revokeable = url;
        setAudioUrl(url);
      } catch (error) {
        console.error("Failed to load recording:", error);
        toast.error("Failed to load recording");
        setRecordingLog(null);
      }
    };
    fetchAudio();

    return () => {
      if (revokeable) URL.revokeObjectURL(revokeable);
    };
  }, [recordingLog?.recordingPublicId]);

  const closeRecording = () => {
    setRecordingLog(null);
    setAudioUrl(null);
  };

  const downloadRecording = async (log) => {
    if (!log?.recordingPublicId) return;
    try {
      const response = await api.get(
        `/recordings/file/${log.recordingPublicId}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `recording-${log._id}.mp3`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download recording");
    }
  };

  // Fetch lead details
  const { data: lead, isLoading } = useQuery({
    queryKey: ["lead", id],
    queryFn: async () => {
      const response = await leadsAPI.getById(id);
      return response.data?.data || null;
    },
    enabled: !!id,
  });

  // Fetch lead's call logs
  const { data: callLogs } = useQuery({
    queryKey: ["leadCallLogs", id],
    queryFn: async () => {
      const response = await callLogsAPI.getAll({ leadId: id, limit: 10, sort: '-createdAt' });
      return response.data?.data || [];
    },
    enabled: !!id,
  });

  // Fetch pipeline stages for dropdown
  const { data: stagesData } = useQuery({
    queryKey: ["pipelineStages"],
    queryFn: async () => {
      const response = await pipelineAPI.getAll();
      return response.data?.data || [];
    },
  });

  // Create stage options for dropdown
  const stageOptions = (stagesData || []).map(stage => ({
    value: stage.key,
    label: stage.name,
    color: stage.color,
  }));

  // Create stage map for display
  const stageMap = (stagesData || []).reduce((acc, stage) => {
    acc[stage.key] = { name: stage.name, color: stage.color || '#6B7280' };
    return acc;
  }, {});

  // Fetch telecallers for assignment
  const { data: telecallers } = useQuery({
    queryKey: ["telecallers"],
    queryFn: async () => {
      const response = await usersAPI.getAll({ role: "telecaller" });
      return response.data?.data || [];
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => leadsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["lead", id]);
      queryClient.invalidateQueries(["leads"]);
      setIsEditing(false);
      toast.success("Lead updated successfully");
    },
    onError: () => toast.error("Failed to update lead"),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => leadsAPI.delete(id),
    onSuccess: () => {
      navigate("/app/leads");
      toast.success("Lead deleted successfully");
    },
    onError: () => toast.error("Failed to delete lead"),
  });

  // Initialize form data when lead loads
  React.useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || "",
        phone: lead.phone || "",
        alternatePhone: lead.alternatePhone || "",
        email: lead.email || "",
        stage: lead.stage || "new_lead",
        status: lead.status || "new",
        source: lead.source || "",
        notes: typeof lead.notes === "string" ? lead.notes : "",
        companyName: lead.companyName || "",
        designation: lead.designation || "",
        industry: lead.industry || "",
        website: lead.website || "",
        gstNumber: lead.gstNumber || "",
        companySize: lead.companySize || "",
        annualRevenue: lead.annualRevenue || "",
        tagsString: Array.isArray(lead.tags) ? lead.tags.join(", ") : "",
        addressStreet: lead.address?.street || "",
        addressCity: lead.address?.city || "",
        addressState: lead.address?.state || "",
        addressPincode: lead.address?.pincode || "",
        addressCountry: lead.address?.country || "",
      });
    }
  }, [lead]);

  const handleSave = () => {
    // Validate form
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    } else if (/\d/.test(formData.name)) {
      errors.name = "Name cannot contain numbers";
    }

    if (!formData.phone.trim()) {
      errors.phone = "Phone is required";
    } else if (!/^[\d\s\-+]+$/.test(formData.phone.trim())) {
      errors.phone = "Phone can only contain digits, +, spaces, and hyphens";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Invalid email format";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    // Assemble nested address + tags array from the flat form fields
    const payload = {
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      alternatePhone: formData.alternatePhone.trim(),
      email: formData.email.trim(),
      stage: formData.stage,
      status: formData.status,
      source: formData.source.trim(),
      notes: formData.notes,
      companyName: formData.companyName.trim(),
      designation: formData.designation.trim(),
      industry: formData.industry.trim(),
      website: formData.website.trim(),
      gstNumber: formData.gstNumber.trim(),
      companySize: formData.companySize.trim(),
      annualRevenue: formData.annualRevenue.trim(),
      tags: (formData.tagsString || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      address: {
        street: formData.addressStreet.trim(),
        city: formData.addressCity.trim(),
        state: formData.addressState.trim(),
        pincode: formData.addressPincode.trim(),
        country: formData.addressCountry.trim(),
      },
    };
    updateMutation.mutate(payload);
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    // Prevent numbers in name
    if (/\d/.test(value)) return;
    setFormData({ ...formData, name: value });
    if (formErrors.name) setFormErrors({ ...formErrors, name: "" });
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits, +, spaces, and hyphens
    if (value && !/^[\d\s\-+]*$/.test(value)) return;
    setFormData({ ...formData, phone: value });
    if (formErrors.phone) setFormErrors({ ...formErrors, phone: "" });
  };

  const handleEmailChange = (e) => {
    setFormData({ ...formData, email: e.target.value });
    if (formErrors.email) setFormErrors({ ...formErrors, email: "" });
  };

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (!lead) {
    return (
      <Layout>
        <div className="p-4 text-center">
          <p className="text-gray-500">Lead not found</p>
          <Button
            variant="primary"
            onClick={() => navigate("/app/leads")}
            className="mt-4"
          >
            Back to Leads
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              icon={<HiOutlineArrowLeft className="w-5 h-5" />}
              onClick={() => navigate("/app/leads")}
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">{lead.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium capitalize"
                  style={{
                    backgroundColor: stageMap[lead.stage]?.color ? `${stageMap[lead.stage].color}20` : '#6B728020',
                    color: stageMap[lead.stage]?.color || '#6B7280',
                  }}
                >
                  {stageMap[lead.stage]?.name || lead.stage?.replace(/_/g, ' ') || 'Unknown'}
                </span>
                <span className="text-sm text-gray-500">
                  Created{" "}
                  {lead.createdAt
                    ? format(new Date(lead.createdAt), "MMM d, yyyy")
                    : "-"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<HiOutlineSave className="w-4 h-4" />}
                  onClick={handleSave}
                  loading={updateMutation.isPending}
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  icon={<HiOutlinePencil className="w-4 h-4" />}
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  icon={<HiOutlineTrash className="w-4 h-4" />}
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
          {/* Lead Info Card */}
          <Card className="lg:col-span-2" padding="p-4 sm:p-6">
            <h2 className="font-semibold text-gray-900 mb-4">
              Lead Information
            </h2>

            {isEditing ? (
              <div className="space-y-5">
                {/* Contact */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-3">
                    Contact
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Name"
                      value={formData.name}
                      onChange={handleNameChange}
                      error={formErrors.name}
                    />
                    <Input
                      label="Phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      error={formErrors.phone}
                    />
                    <Input
                      label="Alternate Phone"
                      value={formData.alternatePhone || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, alternatePhone: e.target.value })
                      }
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={handleEmailChange}
                      error={formErrors.email}
                    />
                    <Select
                      label="Stage"
                      options={stageOptions}
                      value={formData.stage}
                      onChange={(e) =>
                        setFormData({ ...formData, stage: e.target.value })
                      }
                    />
                    <Select
                      label="Status"
                      options={statusOptions}
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({ ...formData, status: e.target.value })
                      }
                    />
                    <Input
                      label="Source"
                      value={formData.source || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, source: e.target.value })
                      }
                      placeholder="e.g. website, referral, campaign"
                    />
                  </div>
                </div>

                {/* Company */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-3">
                    Company
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Company Name"
                      value={formData.companyName || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, companyName: e.target.value })
                      }
                    />
                    <Input
                      label="Designation"
                      value={formData.designation || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, designation: e.target.value })
                      }
                    />
                    <Input
                      label="Industry"
                      value={formData.industry || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, industry: e.target.value })
                      }
                    />
                    <Input
                      label="Website"
                      value={formData.website || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, website: e.target.value })
                      }
                      placeholder="https://..."
                    />
                    <Input
                      label="GST Number"
                      value={formData.gstNumber || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, gstNumber: e.target.value })
                      }
                    />
                    <Input
                      label="Company Size"
                      value={formData.companySize || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, companySize: e.target.value })
                      }
                      placeholder="e.g. 11-50"
                    />
                    <Input
                      label="Annual Revenue"
                      value={formData.annualRevenue || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, annualRevenue: e.target.value })
                      }
                    />
                    <Input
                      label="Tags (comma separated)"
                      value={formData.tagsString || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, tagsString: e.target.value })
                      }
                      placeholder="e.g. VIP, Wholesale"
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-3">
                    Address
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Input
                        label="Street"
                        value={formData.addressStreet || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, addressStreet: e.target.value })
                        }
                        placeholder="Street / building / area"
                      />
                    </div>
                    <Input
                      label="City"
                      value={formData.addressCity || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, addressCity: e.target.value })
                      }
                    />
                    <Input
                      label="State"
                      value={formData.addressState || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, addressState: e.target.value })
                      }
                    />
                    <Input
                      label="Pincode"
                      value={formData.addressPincode || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, addressPincode: e.target.value })
                      }
                    />
                    <Input
                      label="Country"
                      value={formData.addressCountry || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, addressCountry: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Notes
                  </label>
                  <textarea
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none"
                    rows={4}
                    value={formData.notes || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    placeholder="Add notes..."
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Contact */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                    Contact
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoField
                      label="Phone"
                      value={lead.phone}
                      icon={<HiOutlinePhone className="w-4 h-4" />}
                    />
                    <InfoField
                      label="Alternate Phone"
                      value={lead.alternatePhone}
                      icon={<HiOutlinePhone className="w-4 h-4" />}
                    />
                    <InfoField
                      label="Email"
                      value={lead.email}
                      icon={<HiOutlineMail className="w-4 h-4" />}
                    />
                    <InfoField
                      label="Source"
                      value={lead.source ? lead.source.replace(/_/g, " ") : ""}
                    />
                    <div>
                      <p className="text-sm text-gray-500">Stage</p>
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium capitalize mt-1"
                        style={{
                          backgroundColor: stageMap[lead.stage]?.color ? `${stageMap[lead.stage].color}20` : '#6B728020',
                          color: stageMap[lead.stage]?.color || '#6B7280',
                        }}
                      >
                        {stageMap[lead.stage]?.name || lead.stage?.replace(/_/g, ' ') || 'Unknown'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <span className={`font-medium status-${lead.status} capitalize`}>
                        {lead.status}
                      </span>
                    </div>
                    <InfoField
                      label="Assigned To"
                      value={
                        lead.assignedTo
                          ? `${lead.assignedTo.firstName} ${lead.assignedTo.lastName}`
                          : ""
                      }
                      icon={<HiOutlineUser className="w-4 h-4" />}
                    />
                    <InfoField
                      label="Created"
                      value={
                        lead.createdAt
                          ? format(new Date(lead.createdAt), "MMM d, yyyy")
                          : ""
                      }
                    />
                  </div>
                </div>

                {/* Company */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                    Company
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoField
                      label="Company Name"
                      value={lead.companyName}
                      icon={<HiOutlineOfficeBuilding className="w-4 h-4" />}
                    />
                    <InfoField
                      label="Designation"
                      value={lead.designation}
                      icon={<HiOutlineIdentification className="w-4 h-4" />}
                    />
                    <InfoField
                      label="Industry"
                      value={lead.industry}
                    />
                    <InfoField
                      label="Website"
                      value={lead.website}
                      icon={<HiOutlineGlobeAlt className="w-4 h-4" />}
                    />
                    <InfoField
                      label="GST Number"
                      value={lead.gstNumber}
                    />
                    <InfoField
                      label="Company Size"
                      value={lead.companySize}
                      icon={<HiOutlineUsers className="w-4 h-4" />}
                    />
                    <InfoField
                      label="Annual Revenue"
                      value={lead.annualRevenue}
                      icon={<HiOutlineCurrencyDollar className="w-4 h-4" />}
                    />
                    <div>
                      <p className="text-sm text-gray-500">Tags</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {lead.tags && lead.tags.length > 0 ? (
                          lead.tags.map((tag, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200"
                            >
                              <HiOutlineTag className="w-3 h-3 mr-1" />
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="font-medium text-gray-900">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                    Address
                  </h3>
                  <div className="flex items-start gap-2">
                    <HiOutlineLocationMarker className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                    <span className="font-medium text-gray-900 break-words">
                      {formatAddress(lead.address) || "-"}
                    </span>
                  </div>
                </div>

                {/* Notes */}
                {lead.notes && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Notes</p>
                    <div className="text-gray-700 bg-gray-50 p-3 rounded-xl whitespace-pre-wrap">
                      {formatNotes(lead.notes)}
                    </div>
                  </div>
                )}

                {/* Lead-level attachments (from quick notes) */}
                {lead.attachments && lead.attachments.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Attachments ({lead.attachments.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {lead.attachments.map((att, aIndex) => {
                        const isImg = isImageAttachment(att);
                        const url = filePublicUrl(att.url);
                        const label = att.originalName || att.fileName || 'Attachment';
                        const cls = "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors max-w-[240px] cursor-pointer";
                        const inner = (
                          <>
                            <HiOutlinePaperClip className="w-4 h-4 flex-shrink-0" />
                            <span className="text-xs font-medium truncate">{label}</span>
                          </>
                        );
                        return isImg ? (
                          <button
                            key={att._id || `${att.fileName}-${aIndex}`}
                            type="button"
                            title={label}
                            className={cls}
                            onClick={() => setPreviewImage(url)}
                          >
                            {inner}
                          </button>
                        ) : (
                          <a
                            key={att._id || `${att.fileName}-${aIndex}`}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={label}
                            className={cls}
                          >
                            {inner}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Right column: Assignment + Recent Calls, stacked to balance the tall Lead Info card */}
          <div className="lg:col-span-1 space-y-4">
            <Card padding="p-4 sm:p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Assignment</h2>
              <div className="space-y-4">
                {lead.assignedTo ? (
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <HiOutlineUserAdd className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Assigned to</p>
                      <p className="font-medium text-gray-900">
                        {lead.assignedTo.firstName} {lead.assignedTo.lastName}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <HiOutlineUserAdd className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <p className="font-medium text-amber-700">Unassigned</p>
                    </div>
                  </div>
                )}
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  icon={<HiOutlineUserAdd className="w-5 h-5" />}
                  onClick={() => setShowAssignModal(true)}
                >
                  {lead.assignedTo ? "Change Assignment" : "Assign Lead"}
                </Button>
              </div>
            </Card>

            {/* Call History */}
            <Card padding="p-4 sm:p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Recent Calls</h2>
              {callLogs && callLogs.length > 0 ? (
                <div className="space-y-3">
                  {callLogs.map((log) => (
                    <div key={log._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          log.status === 'connected' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                        }`}>
                          <HiOutlinePhone className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {log.callType === 'outgoing' ? 'Outgoing Call' : log.callType === 'incoming' ? 'Incoming Call' : 'Missed Call'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {log.createdAt ? format(new Date(log.createdAt), 'MMM d, yyyy h:mm a') : '-'}
                          </p>
                          {log.notes && (
                            <p className="text-xs text-gray-400 mt-1 line-clamp-1">{log.notes}</p>
                          )}
                          {log.attachments && log.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {log.attachments.map((att, aIndex) => {
                                const isImg = isImageAttachment(att);
                                const url = filePublicUrl(att.url);
                                const label = att.originalName || att.fileName || 'Attachment';
                                const commonCls = "inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors max-w-[220px] cursor-pointer";
                                const inner = (
                                  <>
                                    <HiOutlinePaperClip className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span className="text-xs font-medium truncate">{label}</span>
                                  </>
                                );
                                return isImg ? (
                                  <button
                                    key={att._id || `${att.fileName}-${aIndex}`}
                                    type="button"
                                    title={label}
                                    className={commonCls}
                                    onClick={() => setPreviewImage(url)}
                                  >
                                    {inner}
                                  </button>
                                ) : (
                                  <a
                                    key={att._id || `${att.fileName}-${aIndex}`}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={label}
                                    className={commonCls}
                                  >
                                    {inner}
                                  </a>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <Badge variant={log.status === 'connected' ? 'success' : 'danger'} size="sm">
                          {log.status?.replace('_', ' ')}
                        </Badge>
                        <p className="text-sm text-gray-500 mt-1">
                          {log.duration ? `${Math.floor(log.duration / 60)}:${(log.duration % 60).toString().padStart(2, '0')}` : '0:00'}
                        </p>
                        {log.recordingPublicId && (
                          <div className="flex items-center justify-end gap-1 mt-2">
                            <button
                              type="button"
                              onClick={() => setRecordingLog(log)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors"
                              title="Play recording"
                            >
                              <HiOutlinePlay className="w-4 h-4" />
                              <span className="text-xs font-medium">Play</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => downloadRecording(log)}
                              className="inline-flex items-center p-1 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                              title="Download recording"
                            >
                              <HiOutlineDownload className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <HiOutlinePhone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No call history yet</p>
                  <p className="text-sm text-gray-400 mt-1">Calls made to this lead will appear here</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Lead"
        size="sm"
      >
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete <strong>{lead.name}</strong>? This
          action cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            loading={deleteMutation.isPending}
          >
            Delete Lead
          </Button>
        </div>
      </Modal>

      {/* Assign Modal */}
      <Modal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        title={lead?.assignedTo ? "Change Assignment" : "Assign Lead"}
        size="sm"
      >
        <div className="space-y-4">
          {lead?.assignedTo && (
            <p className="text-sm text-amber-600">
              Currently assigned to:{" "}
              <strong>
                {lead.assignedTo.firstName} {lead.assignedTo.lastName}
              </strong>
            </p>
          )}
          <Select
            label="Select Telecaller"
            options={(telecallers || []).map((u) => ({
              value: u._id,
              label: `${u.firstName} ${u.lastName}`,
            }))}
            placeholder="Choose a telecaller"
            value={lead?.assignedTo?._id || ""}
            onChange={(e) => {
              updateMutation.mutate(
                { assignedTo: e.target.value },
                {
                  onSuccess: () => {
                    setShowAssignModal(false);
                    toast.success("Lead assigned successfully");
                  },
                  onError: () => toast.error("Failed to assign lead"),
                },
              );
            }}
          />
          <div className="flex gap-3 justify-end pt-4">
            <Button variant="ghost" onClick={() => setShowAssignModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                const selectEl = document.querySelector("select");
                if (!selectEl?.value) {
                  toast.error("Select a telecaller");
                  return;
                }
                updateMutation.mutate(
                  { assignedTo: selectEl.value },
                  {
                    onSuccess: () => {
                      setShowAssignModal(false);
                      toast.success("Lead assigned successfully");
                    },
                    onError: () => toast.error("Failed to assign lead"),
                  },
                );
              }}
              loading={updateMutation.isPending}
            >
              {lead?.assignedTo ? "Reassign" : "Assign"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Recording Player Modal */}
      <Modal
        isOpen={!!recordingLog}
        onClose={closeRecording}
        title="Call Recording"
        size="md"
      >
        {recordingLog && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-xl">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">
                    {recordingLog.callType === "outgoing"
                      ? "Outgoing Call"
                      : recordingLog.callType === "incoming"
                      ? "Incoming Call"
                      : "Missed Call"}
                  </p>
                  <p className="text-sm text-gray-500">
                    {recordingLog.createdAt
                      ? format(new Date(recordingLog.createdAt), "MMM d, yyyy h:mm a")
                      : "-"}
                  </p>
                </div>
                <Badge
                  variant={recordingLog.status === "connected" ? "success" : "danger"}
                  size="sm"
                >
                  {recordingLog.status?.replace("_", " ")}
                </Badge>
              </div>
              {audioUrl ? (
                <audio controls className="w-full" src={audioUrl}>
                  Your browser does not support the audio element.
                </audio>
              ) : (
                <div className="flex items-center justify-center py-4">
                  <Spinner size="sm" />
                  <span className="ml-2 text-gray-500 text-sm">Loading recording...</span>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineDownload className="w-4 h-4" />}
                onClick={() => downloadRecording(recordingLog)}
              >
                Download
              </Button>
              <Button variant="ghost" size="sm" onClick={closeRecording}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Attachment image preview lightbox */}
      <ImagePreviewModal src={previewImage} onClose={() => setPreviewImage(null)} />
    </Layout>
  );
};

export default LeadDetails;
